import React, { useRef, useState, useEffect } from "react";
import {
    Tooltip,
    Popover,
    Input,
    Button,
    Select,
    Alert,
    message,
} from "antd";
import { AlertTriangle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { markSpamChannelCustomer, getChannelCustomerDetail } from "../../../api/ChannelCustomer";

const { TextArea } = Input;

const MAX_IMAGES = 3;

interface LocalFile {
    file: File;
    preview: string;
}

interface ReportDropdownProps {
    customerId?: number;
    /** Gọi sau khi báo xấu thành công để parent nạp lại số/danh sách spam */
    onReported?: () => void;
}

const ReportDropdown = ({ customerId, onReported }: ReportDropdownProps) => {
    const { t } = useTranslation();
    const [openDropdown, setOpenDropdown] = useState(false);
    const [content, setContent] = useState("");
    const [phone, setPhone] = useState("");
    const [customerName, setCustomerName] = useState("");
    const [files, setFiles] = useState<LocalFile[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!customerId || !openDropdown) return;
        getChannelCustomerDetail(customerId).then(res => {
            if (res.status && res.data) {
                setCustomerName(res.data.display_name || "");
                setPhone(res.data.phone || res.data.external_user_id || "");
            }
        });
    }, [customerId, openDropdown]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = Array.from(e.target.files || []);
        if (!selected.length) return;
        setFiles(prev => {
            const remaining = MAX_IMAGES - prev.length;
            if (remaining <= 0) {
                message.warning(`Tối đa ${MAX_IMAGES} ảnh`);
                return prev;
            }
            const toAdd = selected.slice(0, remaining).map(file => ({
                file,
                preview: URL.createObjectURL(file),
            }));
            return [...prev, ...toAdd];
        });
        e.target.value = "";
    };

    const handleRemoveFile = (index: number) => {
        setFiles(prev => {
            URL.revokeObjectURL(prev[index].preview);
            return prev.filter((_, i) => i !== index);
        });
    };

    const handleSubmit = async () => {
        if (!customerId || !content.trim()) return;
        setSubmitting(true);
        message.loading({ content: t("report_bad.sending"), key: "report" });
        const payload: { is_spam: number; note: string; image_1?: File; image_2?: File; image_3?: File } = {
            is_spam: 1,
            note: content.trim(),
        };
        if (files[0]) payload.image_1 = files[0].file;
        if (files[1]) payload.image_2 = files[1].file;
        if (files[2]) payload.image_3 = files[2].file;
        const res = await markSpamChannelCustomer(customerId, payload);
        setSubmitting(false);
        if (res.status) {
            message.success({ content: t("report_bad.success"), key: "report" });
            setOpenDropdown(false);
            setContent("");
            setFiles([]);
            onReported?.();
        } else {
            message.error({ content: t("report_bad.error") || "Gửi báo xấu thất bại", key: "report" });
        }
    };

    const dropdownContent = (
        <div className="w-[380px] max-w-[380px] bg-white dark:bg-[#1f1f1f] text-black dark:text-white rounded-lg">

            {/* Header khách hàng */}
            <div className="flex items-center justify-center text-[18px] font-semibold pb-4 mb-4 border-b dark:border-[#2a2a2a]">{t("report_bad.title")}</div>
            <div className="flex items-center gap-3 mb-3">
                <div className="w-[40px] h-[40px] rounded-full bg-orange-200 flex items-center justify-center font-bold text-orange-600">
                    {customerName ? customerName.slice(0, 2).toUpperCase() : "KH"}
                </div>
                <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t("report_bad.customer")}
                    </div>
                    <div className="font-semibold">
                        {customerName || "—"}
                    </div>
                </div>
            </div>

            {/* Alert */}
            <Alert
                message={t("report_bad.alert")}
                type="info"
                showIcon
                closable
                className="mb-3 text-[14px]"
            />

            {/* Select SĐT */}
            <div className="mb-3">
                <div className="text-sm mb-1">
                    {t("report_bad.phone_label")}<span className="text-red-500">*</span>
                </div>
                <Select
                    value={phone || undefined}
                    onChange={setPhone}
                    className="w-full"
                    options={phone ? [{ label: phone, value: phone }] : []}
                />
            </div>

            {/* Nội dung */}
            <div className="mb-2 text-sm">
                {t("report_bad.content_label")}<span className="text-red-500">*</span>
            </div>

            <TextArea
                rows={4}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t("report_bad.content_placeholder")}
            />

            {/* Upload */}
            <div className="mt-3 flex gap-2 flex-wrap">
                {files.length < MAX_IMAGES && (
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className="w-[70px] h-[70px] border border-dashed flex flex-col items-center justify-center rounded cursor-pointer text-sm"
                    >
                        <div>📷</div>
                        <div>{t("report_bad.upload_photo")}</div>
                        <div className="text-xs text-gray-400">({files.length}/{MAX_IMAGES})</div>
                    </div>
                )}
                {files.map((item, index) => (
                    <div key={index} className="relative group">
                        <div
                            onClick={() => handleRemoveFile(index)}
                            className="absolute top-[-6px] right-[-6px] w-[18px] h-[18px] rounded-full bg-black/70 text-white text-[12px] flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition"
                        >
                            <X size={12} />
                        </div>
                        <img
                            src={item.preview}
                            className="w-[70px] h-[70px] object-cover rounded"
                            alt="preview"
                        />
                    </div>
                ))}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFileChange}
                />
            </div>

            {/* Button */}
            <div className="flex justify-end mt-4">
                <Button
                    loading={submitting}
                    disabled={!content.trim() || submitting}
                    style={content.trim() && !submitting
                        ? { backgroundColor: "#3e91AB", borderColor: "#3e91AB", color: "#fff" }
                        : { backgroundColor: "#d1d5db", borderColor: "#d1d5db", color: "#fff", cursor: "not-allowed" }
                    }
                    onClick={handleSubmit}
                >
                    {t("report_bad.submit")}
                </Button>
            </div>
        </div>
    );

    return (
        <Popover
            content={dropdownContent}
            trigger="click"
            open={openDropdown}
            onOpenChange={setOpenDropdown}
            placement="bottomRight"
        >
            <Tooltip title={t("report_bad.title")}>
                <div className="w-[24px] h-[24px] flex items-center justify-center rounded-[4px] bg-red-500 text-white cursor-pointer">
                    <AlertTriangle size={16} />
                </div>
            </Tooltip>
        </Popover>
    );
};

export default ReportDropdown;